import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Manages all meta tags, Open Graph, Twitter Cards, and Structured Data
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description
 * @param {string} props.keywords - Meta keywords
 * @param {string} props.canonical - Canonical URL
 * @param {string} props.image - OG/Twitter image URL
 * @param {string} props.type - OG type (website, article, product)
 * @param {Object} props.structuredData - JSON-LD structured data
 * @param {boolean} props.noIndex - Set to true to prevent indexing
 */
export const SEO = ({
  title = 'Suvidha Travel - B2B Travel Portal | Best Tour Packages in India',
  description = 'Suvidha Travel offers wholesale B2B rates for travel agents. Explore premium tour packages for Himachal, Kashmir, Kerala, Rajasthan, Ladakh, and more. Best prices guaranteed.',
  keywords = 'travel packages, tour packages, B2B travel, Himachal tour, Kashmir tour, Kerala tour, Rajasthan tour, India travel, holiday packages, honeymoon packages',
  canonical,
  image = 'https://suvidhatravel.in/og-image.jpg',
  type = 'website',
  structuredData,
  noIndex = false,
  author = 'Suvidha Travel',
  publishedTime,
  modifiedTime
}) => {
  const siteUrl = 'https://suvidhatravel.in';
  
  // Ensure title is always a valid non-empty string
  const defaultTitle = 'Suvidha Travel - B2B Travel Portal';
  let safeTitle = defaultTitle;
  
  if (title && typeof title === 'string' && title.trim().length > 0) {
    safeTitle = title.trim();
  }
  
  const fullTitle = safeTitle.includes('Suvidha Travel') ? safeTitle : safeTitle + ' | Suvidha Travel';
  const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href : siteUrl);
  
  // Direct DOM manipulation for reliable SEO tag injection
  // react-helmet-async v2.x has issues with React 18 lazy loading
  if (typeof document !== 'undefined') {
    document.title = fullTitle;
    
    // Helper to set or create meta tags
    const setMeta = (attr, attrValue, content) => {
      let tag = document.querySelector(`meta[${attr}="${attrValue}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    
    // Helper to set or create link tags
    const setLink = (rel, href) => {
      let tag = document.querySelector(`link[rel="${rel}"]`);
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', href);
    };
    
    // Canonical URL
    setLink('canonical', canonicalUrl);
    
    // Basic meta
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords);
    setMeta('name', 'author', author);
    setMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // Open Graph
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:site_name', 'Suvidha Travel');
    
    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);
    
    // hreflang
    let hreflangEN = document.querySelector('link[hreflang="en-in"]');
    if (!hreflangEN) {
      hreflangEN = document.createElement('link');
      hreflangEN.setAttribute('rel', 'alternate');
      hreflangEN.setAttribute('hreflang', 'en-in');
      document.head.appendChild(hreflangEN);
    }
    hreflangEN.setAttribute('href', canonicalUrl);
  }

  // Default Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    name: 'Suvidha Travel',
    alternateName: 'Suvidha Travel India',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Leading B2B travel agency in India offering wholesale rates for tour packages across Himachal, Kashmir, Kerala, Rajasthan, Ladakh and more.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '1440, Galaxy Diamond Plaza, Sec-4 Near Gaur City Mall',
      addressLocality: 'Greater Noida West',
      addressRegion: 'Uttar Pradesh',
      postalCode: '201009',
      addressCountry: 'IN'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '28.5921',
      longitude: '77.4264'
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+91-8585997177',
        contactType: 'sales',
        areaServed: 'IN',
        availableLanguage: ['English', 'Hindi']
      },
      {
        '@type': 'ContactPoint',
        telephone: '+91-9911061103',
        contactType: 'customer service',
        areaServed: 'IN',
        availableLanguage: ['English', 'Hindi']
      }
    ],
    sameAs: [
      'https://www.facebook.com/suvidhatravel',
      'https://www.instagram.com/suvidhatravel',
      'https://twitter.com/suvidhatravel',
      'https://www.linkedin.com/company/suvidhatravel'
    ],
    priceRange: '$$',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '19:00'
      }
    ]
  };

  // Website Schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Suvidha Travel',
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/packages?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <>
      {/* Structured Data (JSON-LD) - injected as script tags */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((schema, index) => (
            <script
              key={index}
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            />
          ))
        ) : (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        )
      )}
    </>
  );
};

// Separate component for JSON-LD structured data (outside of Helmet)
export const StructuredData = ({ organizationSchema, websiteSchema, customData }) => {
  return (
    <>
      {organizationSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
        />
      )}
      {websiteSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
        />
      )}
      {customData && Array.isArray(customData) ? (
        customData.map((data, index) => (
          <script
            key={`sd-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
          />
        ))
      ) : customData ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(customData) }}
        />
      ) : null}
    </>
  );
};

/**
 * Generate Tour Package Schema
 */
export const generatePackageSchema = (pkg, baseUrl = 'https://suvidhatravel.in') => {
  const lowestPrice = pkg.price_table?.reduce((min, row) => {
    const price = row.min2_pax || 0;
    return price < min && price > 0 ? price : min;
  }, Infinity) || pkg.price || 0;

  const highestPrice = pkg.price_table?.reduce((max, row) => {
    const price = row.min2_pax || 0;
    return price > max ? price : max;
  }, 0) || pkg.price || 0;

  return {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name: pkg.title,
    description: pkg.description || `Explore ${pkg.destination} with our ${pkg.nights}N/${pkg.nights + 1}D tour package. Includes hotels, transfers, and sightseeing.`,
    url: `${baseUrl}/st_tour/${pkg.slug || pkg.id}`,
    image: pkg.image_url || `${baseUrl}/default-package.jpg`,
    touristType: ['Couple', 'Family', 'Group'],
    itinerary: {
      '@type': 'ItemList',
      numberOfItems: pkg.itinerary?.length || pkg.nights + 1,
      itemListElement: pkg.itinerary?.map((day, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: day.title || `Day ${index + 1}`,
        description: day.description || ''
      })) || []
    },
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: lowestPrice,
      highPrice: highestPrice,
      priceCurrency: 'INR',
      availability: pkg.is_available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      validFrom: pkg.validity || new Date().toISOString().split('T')[0],
      seller: {
        '@type': 'TravelAgency',
        name: 'Suvidha Travel',
        url: baseUrl
      }
    },
    provider: {
      '@type': 'TravelAgency',
      name: 'Suvidha Travel',
      url: baseUrl,
      telephone: '+91-8585997177'
    },
    includesAttraction: pkg.highlights?.map(h => ({
      '@type': 'TouristAttraction',
      name: h
    })) || [],
    duration: `P${pkg.nights + 1}D`,
    subjectOf: {
      '@type': 'CreativeWork',
      abstract: pkg.description
    }
  };
};

/**
 * Generate Product Schema for Package (Alternative)
 */
export const generateProductSchema = (pkg, baseUrl = 'https://suvidhatravel.in') => {
  const lowestPrice = pkg.price_table?.reduce((min, row) => {
    const price = row.min2_pax || 0;
    return price < min && price > 0 ? price : min;
  }, Infinity) || pkg.price || 0;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: pkg.title,
    description: pkg.description || `${pkg.nights}N/${pkg.nights + 1}D tour package to ${pkg.destination}`,
    image: pkg.image_url,
    url: `${baseUrl}/st_tour/${pkg.slug || pkg.id}`,
    brand: {
      '@type': 'Brand',
      name: 'Suvidha Travel'
    },
    offers: {
      '@type': 'Offer',
      price: lowestPrice,
      priceCurrency: 'INR',
      availability: pkg.is_available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: 'Suvidha Travel'
      }
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '150',
      bestRating: '5',
      worstRating: '1'
    }
  };
};

/**
 * Generate Breadcrumb Schema
 */
export const generateBreadcrumbSchema = (items, baseUrl = 'https://suvidhatravel.in') => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${baseUrl}${item.url}` : undefined
    }))
  };
};

/**
 * Generate FAQ Schema
 */
export const generateFAQSchema = (faqs) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

export default SEO;
